import { cn } from '@/lib/utils';
import { Link } from '@inertiajs/react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { AuthorByline } from './AuthorByline';
import { articleUrl, itemVariants, sectionVariants } from './constants';
import type { JournalFeaturedAuthor, JournalGuide, JournalHeroData } from './types';

/** Rosewood video URL — used to conditionally show the info overlay */
const ROSEWOOD_VIDEO = 'https://cdnweb.luxrallytravel.com/parity-migration/2025/08/de567095-10a8-4840-9a9b-42d6d5a16450.mp4';

interface HeroSectionProps {
    hero: JournalHeroData | null;
    trending: JournalGuide[];
    latest: JournalGuide[];
    featuredAuthor: JournalFeaturedAuthor | null;
    /**
     * Homepage only: the featured image takes a taller crop (9:8 from lg) and
     * rises above the side columns so it reaches toward the founder quote
     * above it (CEO-pull-quote.png). /journal leaves this off.
     */
    featuredTall?: boolean;
}

/** Daily-rotated author spotlight — appears below the Latest column. */
function FeaturedAuthorCard({ author }: { author: JournalFeaturedAuthor }) {
    return (
        <Link href={`/journal/author/${author.slug}`} className="group block pt-6">
            <div className="flex items-center gap-4">
                {author.avatar && <img src={author.avatar} alt={author.name} className="h-14 w-14 flex-shrink-0 rounded-full object-cover" />}
                <div className="min-w-0">
                    <span className="lux-caps lux-caps-sm block text-[#0e2727]/55">Contributor Spotlight</span>
                    <h3 className="lux-didot mt-0.5 text-[17px] leading-snug text-[#0e2727] transition-colors group-hover:text-[#0e2727]/75">
                        {author.name}
                    </h3>
                </div>
            </div>
            {author.bio && (
                <p
                    className="lux-body mt-3 line-clamp-3 text-[12px] leading-relaxed text-[#0e2727]/65 italic"
                    style={{ fontFamily: "'Didot', 'Playfair Display', Georgia, serif" }}
                >
                    {author.bio}
                </p>
            )}
            <span className="mt-3 inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] text-[#0e2727]/55 uppercase transition-colors group-hover:text-[#0e2727]">
                {author.article_count} articles
                <ArrowRight className="h-3 w-3" />
            </span>
        </Link>
    );
}

/**
 * Trending column card. Desktop (lg+): image on top, "Trending" kicker, title,
 * excerpt, byline. Mobile: a row — title + byline on the left, square thumb on
 * the right (Mobile-app-bar.png, 22 Sep 2026); the kicker and excerpt drop out
 * because the section heading carries the label.
 */
function TrendingCard({ guide }: { guide: JournalGuide }) {
    return (
        <Link href={articleUrl(guide.slug)} className="group flex flex-row items-start gap-4 lg:flex-col lg:gap-0">
            <div className="order-1 min-w-0 flex-1 lg:order-2 lg:pt-4">
                <span className="lux-caps lux-caps-sm hidden leading-none text-[#0e2727]/60 lg:block">Trending</span>
                <h3 className="lux-didot text-[20px] leading-snug text-[#0e2727] transition-colors group-hover:text-[#0e2727]/75 lg:mt-1 lg:text-[17px]">
                    {guide.title}
                </h3>
                {/* Wrapper carries the breakpoint toggle: line-clamp needs display:-webkit-box
                    on the <p> itself, so no display utility may sit on it. */}
                {guide.excerpt && (
                    <div className="hidden lg:block">
                        <p className="lux-body mt-2 line-clamp-2 text-[12px] leading-relaxed text-[#0e2727]/65">{guide.excerpt}</p>
                    </div>
                )}
                {guide.author_name && <AuthorByline name={guide.author_name} className="mt-2 text-[#0e2727]/55" />}
            </div>
            {guide.main_image && (
                <div className="relative order-2 aspect-square w-[36%] max-w-[140px] shrink-0 overflow-hidden lg:order-1 lg:aspect-[3/2] lg:w-full lg:max-w-none">
                    <img
                        src={guide.main_image}
                        alt={guide.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                    />
                </div>
            )}
        </Link>
    );
}

/** Compact horizontal card — text on the left, thumb on the right (square on mobile, tall portrait from lg). */
function LatestCard({ guide }: { guide: JournalGuide }) {
    return (
        <Link href={articleUrl(guide.slug)} className="group flex items-start gap-4 lg:items-stretch lg:gap-3">
            <div className="flex min-w-0 flex-1 flex-col lg:justify-center">
                <h3 className="lux-didot text-[20px] leading-snug text-[#0e2727] transition-colors group-hover:text-[#0e2727]/70 lg:text-[15px]">{guide.title}</h3>
                {guide.excerpt && (
                    <div className="hidden lg:block">
                        <p className="lux-body mt-1 line-clamp-2 text-[12px] text-[#0e2727]/60">{guide.excerpt}</p>
                    </div>
                )}
                {guide.author_name && <AuthorByline name={guide.author_name} className="mt-2 text-[#0e2727]/55" />}
            </div>
            {guide.main_image && (
                <div className="relative aspect-square w-[36%] max-w-[140px] shrink-0 overflow-hidden lg:aspect-[3/4] lg:w-20 lg:max-w-none">
                    <img
                        src={guide.main_image}
                        alt={guide.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                    />
                </div>
            )}
        </Link>
    );
}

export default function HeroSection({ hero, trending, latest, featuredAuthor, featuredTall = false }: HeroSectionProps) {
    const shouldReduceMotion = useReducedMotion();
    const videoRef = useRef<HTMLVideoElement>(null);
    const [overlayOpen, setOverlayOpen] = useState(false);

    useEffect(() => {
        videoRef.current?.play().catch(() => {});
    }, []);

    if (!hero) return null;

    const isVideo = hero.media_type === 'video';
    const showRosewoodOverlay = hero.media_url === ROSEWOOD_VIDEO;

    const featured = (
        <div className="flex flex-col">
            <Link
                href={hero.cta_url}
                className={cn('group relative block aspect-[3/2] overflow-hidden sm:aspect-[4/3]', featuredTall && 'lg:aspect-[9/8]')}
            >
                {isVideo ? (
                    <video
                        ref={videoRef}
                        className="absolute inset-0 h-full w-full object-cover"
                        style={shouldReduceMotion ? undefined : { animation: 'expandingHeroBreath 20s ease-in-out infinite' }}
                        loop
                        muted
                        playsInline
                        preload="metadata"
                        poster={hero.article.main_image ?? undefined}
                    >
                        <source src={hero.media_url} type="video/mp4" />
                    </video>
                ) : (
                    <img
                        src={hero.media_url}
                        alt={hero.title}
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                        style={shouldReduceMotion ? undefined : { animation: 'expandingHeroBreath 20s ease-in-out infinite' }}
                    />
                )}

                {showRosewoodOverlay && (
                    <div className="absolute right-4 bottom-4 z-20 hidden md:right-6 md:bottom-6 md:block">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setOverlayOpen((o) => !o);
                            }}
                            className={cn(
                                'group/info flex h-8 w-8 items-center justify-center rounded-full',
                                'bg-white/10 backdrop-blur-sm transition-all duration-300 hover:bg-white/20',
                                overlayOpen && 'bg-white/20',
                            )}
                        >
                            <span className="lux-didot text-sm text-white/80 transition-colors duration-300 group-hover/info:text-white">?</span>
                        </button>
                        <AnimatePresence>
                            {overlayOpen && (
                                <>
                                    <div
                                        className="fixed inset-0 z-40"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setOverlayOpen(false);
                                        }}
                                    />
                                    <motion.div
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 6 }}
                                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                                        className="absolute right-0 bottom-full z-50 mb-3 w-72 border border-white/10 bg-[#0e2727]/90 p-5 backdrop-blur-xl"
                                    >
                                        <h4 className="lux-didot lux-didot-sm mb-1.5 text-[#F5F5F5]">Rosewood Schloss Fuschl</h4>
                                        <p className="lux-caps lux-caps-sm mb-2 text-[#F5F5F5]/40">Salzburg, Austria</p>
                                        <p className="lux-body lux-body-sm text-[#F5F5F5]/70">A lakeside retreat nestled in the Austrian Alps.</p>
                                    </motion.div>
                                </>
                            )}
                        </AnimatePresence>
                    </div>
                )}
            </Link>

            <div className="mt-6 text-center">
                {/* Mobile: no rule under the label (Mobile-app-bar.png); desktop keeps it. */}
                <span className="lux-caps lux-caps-sm mb-6 block text-[#0e2727]/50 lg:mb-3">Featured</span>
                <div className="mx-auto mb-4 hidden h-px w-16 bg-[#0e2727]/20 lg:block" />
                <Link href={hero.cta_url}>
                    <h2
                        className="lux-didot text-[#0e2727] transition-colors hover:text-[#0e2727]/75"
                        style={{ fontSize: 'clamp(1.875rem, 3.25vw, 3rem)', lineHeight: 1.08 }}
                    >
                        {hero.title}
                    </h2>
                </Link>
                {hero.subtitle && <p className="lux-body lux-body-md mx-auto mt-4 line-clamp-3 max-w-xl text-[#0e2727]/65">{hero.subtitle}</p>}
                <Link
                    href={hero.cta_url}
                    className="mt-5 inline-flex items-center gap-2 text-[#0e2727]/60 transition-colors duration-300 hover:text-[#0e2727]"
                >
                    <span className="lux-caps lux-caps-sm">{hero.cta_text}</span>
                    <ArrowRight className="h-3.5 w-3.5" />
                </Link>
            </div>
        </div>
    );

    // Desktop: centred label between hairlines. Mobile: a hairline above and the
    // label flush left (the mock's "TRENDING" / "LATEST" section marks).
    const columnHeading = (label: string, { mobileOnly = false }: { mobileOnly?: boolean } = {}) => (
        <div className={cn('mb-10 flex items-center gap-3 border-t border-[#0e2727]/15 pt-6 lg:mb-5 lg:border-t-0 lg:pt-0', mobileOnly && 'lg:hidden')}>
            <div className="hidden h-px flex-1 bg-[#0e2727]/15 lg:block" />
            <span className="lux-caps lux-caps-sm whitespace-nowrap text-[#0e2727]/60" style={{ letterSpacing: '0.35em' }}>
                {label}
            </span>
            <div className="hidden h-px flex-1 bg-[#0e2727]/15 lg:block" />
        </div>
    );

    return (
        <motion.section
            className="bg-[#fdfaf3]"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            variants={shouldReduceMotion ? undefined : sectionVariants}
        >
            <div className="lux-space-px pt-8 pb-12 lg:pt-12 lg:pb-16">
                {/* Mobile order (below lg): Featured → Trending → Latest, as the mobile
                    mock. Desktop keeps the three-column spread: Trending · Featured · Latest. */}
                <div className="mx-auto grid max-w-[1400px] gap-10 lg:grid-cols-12 lg:gap-12">
                    {/* Left — Trending (richer cards; desktop header intentionally omitted) */}
                    <motion.div
                        className={cn('order-2 lg:order-none lg:col-span-3', featuredTall && 'lg:pt-[75px]')}
                        variants={shouldReduceMotion ? undefined : itemVariants}
                    >
                        {trending.length > 0 && (
                            <>
                                {columnHeading('Trending', { mobileOnly: true })}
                                <div className="flex flex-col gap-8 lg:gap-10">
                                    {trending.map((guide) => (
                                        <TrendingCard key={guide.id} guide={guide} />
                                    ))}
                                </div>
                            </>
                        )}
                    </motion.div>

                    {/* Center — Featured */}
                    <motion.div className="order-1 lg:order-none lg:col-span-6" variants={shouldReduceMotion ? undefined : itemVariants}>
                        {featured}
                    </motion.div>

                    {/* Right — Latest (compact horizontal cards) + daily featured author */}
                    <motion.div
                        className={cn('order-3 lg:order-none lg:col-span-3', featuredTall && 'lg:pt-[75px]')}
                        variants={shouldReduceMotion ? undefined : itemVariants}
                    >
                        {latest.length > 0 && (
                            <>
                                {columnHeading('Latest')}
                                <div className="flex flex-col gap-8 lg:gap-0 lg:divide-y lg:divide-[#0e2727]/15">
                                    {latest.slice(0, 3).map((guide) => (
                                        <div key={guide.id} className="lg:py-4 lg:first:pt-0 lg:last:pb-0">
                                            <LatestCard guide={guide} />
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                        {featuredAuthor && (
                            <div className="mt-8 border-t border-[#0e2727]/15">
                                <FeaturedAuthorCard author={featuredAuthor} />
                            </div>
                        )}
                    </motion.div>
                </div>
            </div>
        </motion.section>
    );
}
